<template>
  <div class="picker-wrapper" :class="{ 'picker-light-bg': lightBackground }">
    <div v-if="canAddNew" class="add-new-field">
      <VTextField placeholder="Add a new Field" type="text" v-model="newField" :maxlength="60" appearance="ghost" class="vivid-input" @input="addFieldError = ''" />
      <VButton connotation="cta" appearance="filled" :label="saving ? 'Adding...' : 'Add'" :pending="saving" @click="addNewField">
        <template #icon>
          <VIcon name="save-line" />
        </template>
      </VButton>
    </div>
    <div v-if="addFieldError" class="add-field-error" role="alert">
      <VIcon name="error-line" :size="-4" connotation="alert" />
      <span>{{ addFieldError }}</span>
    </div>
    <VSearchableSelect
      :modelValue="modelValue || ''"
      @update:modelValue="onFieldSelected"
      placeholder="Search and select custom field"
      appearance="ghost"
      class="vivid-select"
      fixed-dropdown
      :loading="loading"
    >
      <VOption
        v-for="(field, index) in allFieldsList"
        :key="index"
        :value="field"
        :text="field"
        :label="field"
      />
      <template #loading-options>
        <span>Loading fields...</span>
      </template>
      <template #no-matches>
        <span>No fields matching this filter!</span>
      </template>
    </VSearchableSelect>
  </div>
</template>

<script setup>
import apiInterceptor from '@/shared/apiInterceptor';
import { environment } from '@/shared/env';
import { ref, computed, onBeforeUnmount } from 'vue';
import { useUIStore } from '@/stores/ui';
import { validateCustomField } from '@/helpers/customFields.helpers';
import { useQueryClient } from '@tanstack/vue-query';
import { useCustomDataFields } from '@/composables/useCustomDataFields';

const props = defineProps({
  modelValue: {
    // Allow null/undefined for the "no selection" state; the template
    // already coerces to '' when rendering.
    type: [String, null],
    default: '',
  },
  canAddNew: {
    type: Boolean,
    default: false
  },
  lightBackground: {
    type: Boolean,
    default: false
  },
  // Kept for backward compatibility — VSearchableSelect handles these internally
  closeOnClickOutside: {
    type: Boolean,
    default: true
  },
  openDropdownDefault: {
    type: Boolean,
    default: false
  },
  // When true (bot builder), a newly added field is held/selected locally and NOT saved via the
  // merchant custom-field API; it is persisted against the bot operation on save instead.
  requirePairedValue: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'dropdown-open', 'dropdown-close'])

const updateToast = (payload) => useUIStore().updateToast(payload);
const updateLoading = (payload) => useUIStore().updateLoading(payload);
const queryClient = useQueryClient();
const { data: allFields, isPending, isFetching, unsubscribe } = useCustomDataFields();
const loading = computed(() => isPending.value || isFetching.value);

const newField = ref();
const saving = ref(false);
const addFieldError = ref('');
// Bot builder: field name added/selected locally only. It's never sent to the merchant
// custom-field API from here; it's saved against the bot operation on save.
const pendingField = ref();

const allFieldsList = computed(() => {
  const fields = [...(allFields.value ?? [])];
  if (pendingField.value && !fields.includes(pendingField.value)) fields.unshift(pendingField.value);
  return fields;
});

const addNewField = async () => {
  addFieldError.value = '';
  try {
    validateCustomField(newField.value, null, false);
    const name = newField.value;
    if (props.requirePairedValue) {
      // Bot builder: don't call the merchant custom-field save API. Hold the field name
      // locally and select it; it's persisted against the bot operation on save instead.
      pendingField.value = name;
      newField.value = undefined;
      emit('update:modelValue', name);
      emit('change', name);
      return;
    }
    saving.value = true;
    const data = await apiInterceptor.post(environment('VUE_APP_API_URL') + `crm/v1/customers/merchant-custom-fields`, { name });
    updateToast({
      open: true,
      message: "Added variable successfully",
      type: "success"
    })
    newField.value = undefined;
    queryClient.invalidateQueries({ queryKey: ['custom-fields'] });
    emit('update:modelValue', data.name);
    emit('change', data.name);
  } catch (error) {
    console.log("Exception", error);
    // Show inline error — toast is not usable when the picker is inside a modal
    // because the modal's z-index stacking context renders the toast behind it.
    addFieldError.value = error.message;
  } finally {
    saving.value = false;
    updateLoading({ isLoading: false });
  }
}

const onFieldSelected = (value) => {
  if (!value) return;
  if (pendingField.value && pendingField.value !== value) pendingField.value = undefined;
  emit('update:modelValue', value);
  emit('change', value);
}

// Cleanup cache subscription on unmount
onBeforeUnmount(() => {
  if (unsubscribe) unsubscribe();
});
</script>

<style scoped lang="scss">
.picker-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.add-new-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vivid-input {
  flex: 1;
}

.vivid-select {
  width: 100%;
}

.add-field-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 5px;
  background: var(--vvd-color-alert-50, #fff3f3);
  color: var(--vvd-color-alert-700, #d32f2f);
  font-size: 13px;
}
</style>

<style lang="scss">
/* Unscoped so it applies to Vivid web component host elements */
.picker-wrapper.picker-light-bg .vivid-input,
.picker-wrapper.picker-light-bg .vivid-select {
  background: #fff !important;
  border-radius: 5px;
}
</style>
